import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { usePageTitle } from '../../contexts/PageTitleContext';
import { fetchShipmentItem, createInventoryReceivedItem, rollbackInventoryReceivedItem, formatReceivingSubmitError, formatReceivingShopifyWarning, formatReceivingRollbackError } from '../../services/receivingAPI';
import { invalidateShipmentDetail } from '../../services/shipmentCache';
import type { AirtableShipmentItem, AirtableInventoryReceivedItem } from '../../types/airtableTypes';
import PrintLabelModal from './PrintLabelModal';
import PrintEventScanResumeHost from '../labels/PrintEventScanResumeHost';
import { DEFAULT_PRINTER_ID } from '@/shared/types/printer.types';
import ReceiptInfoModal from './ReceiptInfoModal';
import ProductInfoModal from '../shared/ProductInfoModal';
import ShipmentDetailNavbar from './ShipmentDetailNavbar';
import './ShipmentItemDetail.css';

interface ProductInfoItem {
  readonly sku: string;
  readonly name?: string;
  readonly itemName?: string;
}

function ShipmentItemDetail() {
  const { shipmentId, shipmentItemId } = useParams<{ shipmentId: string; shipmentItemId: string }>();
  const [shipmentItem, setShipmentItem] = useState<AirtableShipmentItem | null>(null);
  const [receivedItems, setReceivedItems] = useState<AirtableInventoryReceivedItem[]>([]);
  const [hasLegacyReceivedItems, setHasLegacyReceivedItems] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [quantity, setQuantity] = useState('');
  const [quantityDamaged, setQuantityDamaged] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: string; text: string } | null>(null);
  const [showOverReceiveConfirm, setShowOverReceiveConfirm] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState<{ qty: number; qtyDamaged: number } | null>(null);
  const [printModalItem, setPrintModalItem] = useState<{ sku: string; quantity: number } | null>(null);
  const [infoModalItem, setInfoModalItem] = useState<AirtableInventoryReceivedItem | null>(null);
  const [productInfoItem, setProductInfoItem] = useState<ProductInfoItem | null>(null);
  const [rollbackTarget, setRollbackTarget] = useState<AirtableInventoryReceivedItem | null>(null);
  const [rollingBack, setRollingBack] = useState(false);
  const [heroImageLoadFailed, setHeroImageLoadFailed] = useState(false);

  const titleSegment = shipmentItem?.sku
    ? `Receive ${shipmentItem.sku}`
    : shipmentItem?.name
      ? `Receive ${shipmentItem.name}`
      : 'Receive Item';
  usePageTitle(titleSegment);

  const loadShipmentItem = useCallback(async () => {
    setHeroImageLoadFailed(false);
    const data = await fetchShipmentItem(shipmentItemId!);
    if (!data) {
      setError('Shipment item not found');
      setLoading(false);
      return;
    }
    
    setShipmentItem(data.shipmentItem);
    setReceivedItems(data.receivedItems || []);
    setHasLegacyReceivedItems(data.hasLegacyReceivedItems || false);
    setLoading(false);
  }, [shipmentItemId]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      await loadShipmentItem();
      if (cancelled) return;
    };

    load();

    return () => { cancelled = true; };
  }, [loadShipmentItem]);

  const executeSubmit = async (qty: number, qtyDamaged: number) => {
    setSubmitting(true);
    setMessage(null);

    try {
      const result = await createInventoryReceivedItem({
        purchaseOrderItemId: shipmentItem!.purchaseOrderItemId!,
        shipmentItemId: shipmentItemId!,
        quantity: qty,
        quantityDamaged: qtyDamaged > 0 ? qtyDamaged : undefined,
      });

      // Invalidate the shipment detail cache since received quantities have changed
      invalidateShipmentDetail(shipmentId!);
      if (shipmentItem?.shipmentId && shipmentItem.shipmentId !== shipmentId) {
        invalidateShipmentDetail(shipmentItem.shipmentId);
      }

      // Check if Shopify sync failed but receipt was recorded
      if (result?.shopifySync && !result.shopifySync.success) {
        setMessage({
          type: 'warning',
          text: formatReceivingShopifyWarning(qty, result.shopifySync),
        });
      } else {
        setMessage({ type: 'success', text: `Successfully recorded ${qty} units received` });
      }
      setQuantity('');
      setQuantityDamaged('');
      
      await loadShipmentItem();
    } catch (err: unknown) {
      setMessage({ type: 'error', text: formatReceivingSubmitError(err) });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const qty = parseInt(quantity, 10);
    if (isNaN(qty) || qty <= 0) {
      setMessage({ type: 'error', text: 'Please enter a valid quantity' });
      return;
    }

    const qtyDamaged = quantityDamaged ? parseInt(quantityDamaged, 10) : 0;
    if (quantityDamaged && (isNaN(qtyDamaged) || qtyDamaged < 0)) {
      setMessage({ type: 'error', text: 'Damaged quantity must be a valid number' });
      return;
    }

    if (qtyDamaged > qty) {
      setMessage({ type: 'error', text: 'Damaged cannot exceed quantity received' });
      return;
    }

    if (!shipmentItem?.purchaseOrderItemId) {
      setMessage({ type: 'error', text: 'Missing purchase order item reference' });
      return;
    }

    const totalReceivedDirect = shipmentItem.totalReceivedDirect ?? 0;
    const quantityShipped = shipmentItem.quantityShipped ?? 0;
    const wouldTotalReceived = totalReceivedDirect + qty;

    if (wouldTotalReceived > quantityShipped) {
      setPendingSubmit({ qty, qtyDamaged });
      setShowOverReceiveConfirm(true);
      return;
    }

    await executeSubmit(qty, qtyDamaged);
  };

  const handleOverReceiveConfirm = async () => {
    setShowOverReceiveConfirm(false);
    if (pendingSubmit) {
      await executeSubmit(pendingSubmit.qty, pendingSubmit.qtyDamaged);
      setPendingSubmit(null);
    }
  };

  const handleOverReceiveCancel = () => {
    setShowOverReceiveConfirm(false);
    setPendingSubmit(null);
  };

  const invalidateCaches = (): void => {
    invalidateShipmentDetail(shipmentId!);
    if (shipmentItem?.shipmentId && shipmentItem.shipmentId !== shipmentId) {
      invalidateShipmentDetail(shipmentItem.shipmentId);
    }
  };

  const openProductInfo = (): void => {
    if (!shipmentItem) return;
    const sku = shipmentItem.sku?.trim();
    if (!sku) return;
    const trimmedName = shipmentItem.name?.trim();
    setProductInfoItem(
      trimmedName
        ? { sku, name: trimmedName, itemName: trimmedName }
        : { sku }
    );
  };

  const handlePrintClick = (
    e: React.MouseEvent | React.KeyboardEvent,
    item: AirtableInventoryReceivedItem
  ): void => {
    e.stopPropagation();
    setPrintModalItem({
      sku: item.sku || shipmentItem?.sku || '',
      quantity: item.quantity,
    });
  };

  const handleRollbackClick = (
    e: React.MouseEvent | React.KeyboardEvent,
    item: AirtableInventoryReceivedItem
  ): void => {
    e.stopPropagation();
    setRollbackTarget(item);
  };

  const handleRollbackCancel = (): void => {
    if (rollingBack) return;
    setRollbackTarget(null);
  };

  const handleRollbackConfirm = async (): Promise<void> => {
    if (!rollbackTarget || !shipmentItemId) return;
    setRollingBack(true);
    setMessage(null);
    try {
      const result = await rollbackInventoryReceivedItem(rollbackTarget.id, shipmentItemId);
      invalidateCaches();
      if (result.shopifyRollback.applied) {
        setMessage({
          type: 'success',
          text: `Rolled back ${rollbackTarget.quantity} units`,
        });
      } else {
        setMessage({
          type: 'warning',
          text: `Rolled back ${rollbackTarget.quantity} units. Shopify was not decremented: ${result.shopifyRollback.skippedReason ?? 'original sync did not succeed'}.`,
        });
      }
      setRollbackTarget(null);
      await loadShipmentItem();
    } catch (err: unknown) {
      setMessage({ type: 'error', text: formatReceivingRollbackError(err) });
    } finally {
      setRollingBack(false);
    }
  };

  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    } catch {
      return '';
    }
  };

  const navbarTitle = shipmentItem?.shipmentName || 'Shipment';
  const shipmentBackPath = `/receive/shipment/${shipmentId}`;

  const navbar = (
    <ShipmentDetailNavbar
      title={navbarTitle}
      backPath={shipmentBackPath}
      closeAriaLabel="Close"
      showSiblingNav={false}
    />
  );

  if (loading) {
    return (
      <div className="shipment-item-detail-container">
        {navbar}
        <h1 style={{ color: '#B22222', textShadow: '-1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff, 1px 1px 0 #fff' }}>
          Loading...
        </h1>
      </div>
    );
  }

  if (error || !shipmentItem) {
    return (
      <div className="shipment-item-detail-container">
        {navbar}
        <h1 style={{ color: '#B22222', textShadow: '-1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff, 1px 1px 0 #fff' }}>
          {error || 'Item not found'}
        </h1>
      </div>
    );
  }

  const totalReceivedDirect = shipmentItem.totalReceivedDirect ?? 0;
  const totalReceivedPOLevel = shipmentItem.totalReceivedPOLevel ?? 0;
  const canOpenProductInfo = Boolean(shipmentItem.sku?.trim());
  const heroImage = shipmentItem.inventoryImageUrl && !heroImageLoadFailed ? (
    <img
      src={shipmentItem.inventoryImageUrl}
      alt={shipmentItem.name || shipmentItem.sku}
      className="shipment-item-detail-image"
      onError={() => setHeroImageLoadFailed(true)}
    />
  ) : (
    <div className="shipment-item-detail-image-placeholder">No image</div>
  );
  const itemName = shipmentItem.name || 'Unnamed Item';

  return (
    <div className="shipment-item-detail-container">
      {navbar}

      <div className="shipment-item-detail-card">
        {canOpenProductInfo ? (
          <button
            type="button"
            className="shipment-item-detail-image-btn"
            aria-label={`Product details for ${shipmentItem.sku}`}
            onClick={openProductInfo}
          >
            {heroImage}
          </button>
        ) : (
          heroImage
        )}

        {canOpenProductInfo ? (
          <button
            type="button"
            className="shipment-item-detail-name shipment-item-detail-name--clickable"
            onClick={openProductInfo}
          >
            {itemName}
          </button>
        ) : (
          <div className="shipment-item-detail-name">{itemName}</div>
        )}

        {canOpenProductInfo ? (
          <button
            type="button"
            className="shipment-item-detail-sku shipment-item-detail-sku--clickable"
            onClick={openProductInfo}
          >
            {shipmentItem.sku}
          </button>
        ) : shipmentItem.sku ? (
          <div className="shipment-item-detail-sku">{shipmentItem.sku}</div>
        ) : null}

        {shipmentItem.purchaseOrderNumber && (
          <div className="shipment-item-detail-po">{shipmentItem.purchaseOrderNumber}</div>
        )}

        <div className="shipment-item-detail-stats">
          <div className="shipment-item-stat">
            <div className="shipment-item-stat-label">Shipped</div>
            <div className="shipment-item-stat-value">{shipmentItem.quantityShipped}</div>
          </div>
          <div className="shipment-item-stat">
            <div className="shipment-item-stat-label">Remaining (PO)</div>
            <div className="shipment-item-stat-value">
              {shipmentItem.quantityRemaining ?? '—'}
            </div>
          </div>
          <div className="shipment-item-stat">
            <div className="shipment-item-stat-label">Received (This Shipment)</div>
            <div className="shipment-item-stat-value">{totalReceivedDirect}</div>
          </div>
          <div className="shipment-item-stat">
            <div className="shipment-item-stat-label">Received (All Shipments)</div>
            <div className="shipment-item-stat-value">{totalReceivedPOLevel}</div>
          </div>
        </div>

        {hasLegacyReceivedItems && (
          <div className="shipment-item-legacy-note">
            Note: Some received inventory was recorded before shipment-level tracking was enabled.
          </div>
        )}

        <form className="shipment-item-receive-form" onSubmit={handleSubmit}>
          <h3>Record Received Inventory</h3>
          <div className="shipment-item-form-row">
            <div className="shipment-item-form-group">
              <label htmlFor="quantity">Quantity</label>
              <input
                type="number"
                id="quantity"
                min="1"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="0"
                disabled={submitting}
              />
            </div>
            <div className="shipment-item-form-group">
              <label htmlFor="quantityDamaged">Damaged (optional)</label>
              <input
                type="number"
                id="quantityDamaged"
                min="0"
                value={quantityDamaged}
                onChange={(e) => setQuantityDamaged(e.target.value)}
                placeholder="0"
                disabled={submitting}
              />
            </div>
          </div>
          <button
            type="submit"
            className="shipment-item-submit-btn"
            disabled={submitting || !quantity}
          >
            {submitting ? 'Submitting...' : 'Record Receipt'}
          </button>
        </form>
      </div>

      {message && (
        <div className={`shipment-item-message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="shipment-item-received-list">
        <h3>Received Items (This Shipment)</h3>
        {receivedItems.length === 0 ? (
          <p className="shipment-item-no-received">No items received yet for this shipment</p>
        ) : (
          receivedItems.map((item) => {
            const isRolledBack = item.rolledBackAt != null && item.rolledBackAt !== '';
            const entryClass = [
              'shipment-item-received-entry',
              'shipment-item-received-entry--clickable',
              isRolledBack ? 'shipment-item-received-entry--rolled-back' : '',
            ].filter(Boolean).join(' ');

            return (
              <div
                key={item.id}
                className={entryClass}
                onClick={() => setInfoModalItem(item)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setInfoModalItem(item);
                  }
                }}
              >
                <div>
                  <span className="shipment-item-received-qty">{item.quantity} units</span>
                  {(item.quantityDamaged ?? 0) > 0 && (
                    <span className="shipment-item-received-damaged">
                      ({item.quantityDamaged} damaged)
                    </span>
                  )}
                </div>
                <div className="shipment-item-received-right">
                  <span className="shipment-item-received-date">
                    {formatDate(item.createdAt)}
                  </span>
                  <button
                    type="button"
                    className="shipment-item-print-icon"
                    title="Print labels"
                    aria-label="Print labels"
                    onClick={(e) => handlePrintClick(e, item)}
                  >
                    🖨️
                  </button>
                  {!isRolledBack && (
                    <button
                      type="button"
                      className="shipment-item-rollback-icon"
                      title="Roll back receipt"
                      aria-label="Roll back receipt"
                      onClick={(e) => handleRollbackClick(e, item)}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {showOverReceiveConfirm && (
        <div className="shipment-item-confirm-overlay">
          <div className="shipment-item-confirm-dialog">
            <h3>Confirm Over-Receive</h3>
            <p>
              This will bring total received to {(shipmentItem?.totalReceivedDirect ?? 0) + (pendingSubmit?.qty ?? 0)} units,
              exceeding the {shipmentItem?.quantityShipped ?? 0} shipped.
            </p>
            <p>Are you sure you want to continue?</p>
            <div className="shipment-item-confirm-buttons">
              <button
                type="button"
                className="shipment-item-confirm-btn shipment-item-confirm-btn--yes"
                onClick={handleOverReceiveConfirm}
              >
                Confirm
              </button>
              <button
                type="button"
                className="shipment-item-confirm-btn shipment-item-confirm-btn--no"
                onClick={handleOverReceiveCancel}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {rollbackTarget && (
        <div className="shipment-item-confirm-overlay">
          <div className="shipment-item-confirm-dialog">
            <h3>Roll Back Receipt</h3>
            <p>
              This will roll back {rollbackTarget.quantity} units and decrement Shopify inventory by the same amount.
            </p>
            <p>Are you sure you want to continue?</p>
            <div className="shipment-item-confirm-buttons">
              <button
                type="button"
                className="shipment-item-confirm-btn shipment-item-confirm-btn--yes"
                onClick={() => { void handleRollbackConfirm(); }}
                disabled={rollingBack}
              >
                {rollingBack ? 'Rolling back...' : 'Confirm'}
              </button>
              <button
                type="button"
                className="shipment-item-confirm-btn shipment-item-confirm-btn--no"
                onClick={handleRollbackCancel}
                disabled={rollingBack}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <ProductInfoModal
        item={productInfoItem}
        onClose={() => setProductInfoItem(null)}
      />

      <ReceiptInfoModal
        item={infoModalItem}
        formatDate={formatDate}
        onClose={() => setInfoModalItem(null)}
      />

      <PrintLabelModal
        isOpen={!!printModalItem}
        onClose={() => setPrintModalItem(null)}
        sku={printModalItem?.sku || ''}
        quantity={printModalItem?.quantity || 1}
      />
      <PrintEventScanResumeHost source="receiving_modal" printerId={DEFAULT_PRINTER_ID} />
    </div>
  );
}

export default ShipmentItemDetail;
